import classnames from 'classnames';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { analyticsListeners, analyticsUtils } from '~/analytics';
import {
  appointmentDraftActions,
  appointmentDraftSelectors,
  journeyStateSelectors,
  shopsActions,
  shopsApi,
  shopsSelectors,
  tokenApi,
} from '~/api';
import {
  apiCallFinished,
  calendarSelectors,
  CalendarWithFilters,
  envConfigSelectors,
  errorMessages,
  errorMessagesActions,
  errorTypes,
  Messages,
  ScreenNav,
  scrollToMessageTypeAnchor,
  TitleWithSubtitle,
  withProgressBar,
  withRequiredPrecondition,
  withRestorableDraft,
} from '~/components';
import { calendarHelpers } from '~/components/calendar-with-filters/helpers/format-slots-data';
import { selectDateSelectors } from '~/routes/select-date/select-date.selectors';
import { isEmpty, isNotEmpty, scrollToAnchor, doScrollToErrorMessage } from '~/utils';
import { eligibleSlotsFilter as defaultEligibleSlotsFilter } from '../../components/calendar-with-filters/date-filters';
import { loadPartners } from '../partners/partners.actions';
import { dateUtils, formatInHoursAndMinutes, msToMinutes } from '../utils';
import {
  createQueryParams,
  loadInitialSlots,
  loadNextSlots,
  loadPreviousSlots,
  setSlots,
} from './select-date.actions';
import styles from './SelectDate.scss';
import { ENV_VARS } from 'ENV_CONFIG';

const phoneConsultationAppointmentId = ENV_VARS.REACT_APP_PHONE_CONSULTATION_APPOINTMENT_ID;
import { isFeatureEnabled } from '~/components/features/utils';
import { FeaturesContext } from '~/components/features/with-features.hoc';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ children }) => children);

const { SLOT_DATE_NOT_SELECTED_ERROR, SLOT_TIME_NOT_SELECTED_ERROR, NO_AVAILABLE_PARTNERS_ERROR } =
  errorTypes;

const { triggerAvailabilityInteractionAnalytics, triggerPartnerGroupChangeAnalytics } =
  analyticsListeners;

const { getPartnerGroupName } = analyticsUtils;

const getEligibleSlotsFilter = (config) =>
  config.Calendar && config.Calendar.eligibleSlotsFilter
    ? config.Calendar.eligibleSlotsFilter
    : defaultEligibleSlotsFilter;

export class SelectDate extends PureComponent {
  state = {
    isAjaxCompleted: false,
    subtitleLinkUrl: undefined,
  };
  static contextType = FeaturesContext;
  constructor(props) {
    super(props);

    const { dispatchSetSlots } = props;

    dispatchSetSlots([]);
  }

  componentDidMount() {
    const {
      isRemoteConfigEnabled,
      shopList,
      dispatchLoadShops,
      searchQuery,
      subtitle: {
        component: subtitleComponent,
        inStoreAppointmentTypeId,
        virtualAppointmentTypeId,
        virtualStoreId,
      },
    } = this.props;

    if (isRemoteConfigEnabled && !shopList.length) {
      dispatchLoadShops(searchQuery);
    }
    this.refineAppointmentAvailability();
    if (subtitleComponent) {
      this.buildSubtitleLinkUrl(inStoreAppointmentTypeId, virtualAppointmentTypeId, virtualStoreId);
    }
    window.scrollTo(0, 0);
  }

  componentDidUpdate({ mainAppointmentDraft: prevMainAppointmentDraft }) {
    const {
      slotErrorMessages,
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,

      dispatchRemoveSingleErrorMessage,
    } = this.props;

    if (slotErrorMessages[SLOT_DATE_NOT_SELECTED_ERROR] && mainAppointmentDraft.dateSlot) {
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    }

    if (
      slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] &&
      mainAppointmentDraft.dateSlot !== prevMainAppointmentDraft.dateSlot
    ) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }

    if (slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] && mainAppointmentDraft.timeSlot.from) {
      if (!isMultipleAppointment && mainAppointmentDraft.timeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      } else if (mainAppointmentDraft.timeSlot.from && relatedAppointmentTimeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      }
    }

    doScrollToErrorMessage(!mainAppointmentDraft.timeSlot.from); // Validate condition to scroll to error message

    if (prevMainAppointmentDraft.dateSlot === '' && mainAppointmentDraft.dateSlot) {
      scrollToAnchor('time-slot-title');
    }
  }

  buildSubtitleLinkUrl = (inStoreAppointmentTypeId, virtualAppointmentTypeId, virtualStoreId) => {
    if (inStoreAppointmentTypeId) {
      this.setState({
        subtitleLinkUrl: `shops?appointmentTypeId=${inStoreAppointmentTypeId}`,
      });
    } else if (virtualAppointmentTypeId && virtualStoreId) {
      tokenApi
        .getInitialToken({
          appointmentTypeId: virtualAppointmentTypeId,
          storeId: virtualStoreId,
          searchQuery: 'England',
        })
        .then((token) =>
          this.setState({
            subtitleLinkUrl: `select-date?token=${token}`,
          }),
        );
    }
  };

  refineAppointmentAvailability = () => {
    const {
      mainAppointmentDraft: {
        appointmentType: { hasGroups, groups, beauty: { priceType } = {} },
      },
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
      dispatchClearErrorMessages,
    } = this.props;

    dispatchClearErrorMessages();
    if (priceType !== 'FIXED' && hasGroups && isEmpty(groups)) {
      dispatchResetErrorMessagesWith([{ type: errorTypes.NO_AVAILABLE_PARTNERS_GROUPS_ERROR }]);
      dispatchApiCallFinished(true);
    } else {
      this.resolveSelectedPartnerId()
        .then(this.checkSlotAvailability)
        .then(this.resolveSlots)
        .catch(this.handleAvailabilityError);
    }
  };

  checkSlotAvailability = (partnerId) => {
    const {
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      relatedAppointmentTypeId,
      dispatchApiCallFinished,
    } = this.props;

    if (!mainAppointmentDraft.dateSlot) {
      return Promise.resolve({
        isDateSlotAvailable: true,
        isTimeSlotAvailable: true,
      });
    }

    dispatchApiCallFinished(false);

    const requestParams = createQueryParams(
      {
        ...mainAppointmentDraft,
        partner: {
          ...mainAppointmentDraft.partner,
          id: partnerId,
        },
      },
      mainAppointmentDraft.dateSlot,
    );

    const {
      dateSlot,
      timeSlot,
      shop: { id: shopId },
      appointmentType: { id: mainAppointmentTypeId },
    } = mainAppointmentDraft;

    return shopsApi
      .fetch(`/${shopId}/slots?${requestParams}`, {}, shopId ? [404] : [])
      .then(({ slots }) => {
        const newDateSlot = calendarHelpers.findDateSlot(slots, dateSlot);
        const newTimeSlot = calendarHelpers.findTimeSlot(
          newDateSlot,
          timeSlot,
          mainAppointmentTypeId,
        );
        const newRelatedAppointmentTimeSlot =
          relatedAppointmentTimeSlot &&
          calendarHelpers.findTimeSlot(
            newDateSlot,
            relatedAppointmentTimeSlot,
            relatedAppointmentTypeId,
          );

        return {
          isDateSlotAvailable: !!newDateSlot && newDateSlot.times.length > 0,
          mainAppointmentTimeSlot: newTimeSlot,
          relatedAppointmentTimeSlot: newRelatedAppointmentTimeSlot,
        };
      })
      .catch(this.handleAvailabilityError);
  };

  resolveSelectedPartnerId = () => {
    const {
      mainAppointmentDraft: { partner: selectedPartner },
      ChosenPartner,
      dispatchLoadPartners,
    } = this.props;

    return ChosenPartner
      ? dispatchLoadPartners().then((partners) =>
          partners.some((partner) => partner.id === selectedPartner.id) ? selectedPartner.id : '',
        )
      : Promise.resolve(selectedPartner.id);
  };

  resolveSlots = ({ isDateSlotAvailable, mainAppointmentTimeSlot, relatedAppointmentTimeSlot }) => {
    const {
      relatedAppointmentTypeId,
      isMultipleAppointment,

      dispatchSelectDateSlot,
      dispatchSelectTimeSlot,
      dispatchSaveAppointmentDraft,
      dispatchSetRelatedAppointmentTimeSlot,
    } = this.props;

    if (!isDateSlotAvailable) {
      dispatchSelectDateSlot();
    } else if (!mainAppointmentTimeSlot) {
      dispatchSelectTimeSlot();
    }

    if (mainAppointmentTimeSlot) {
      dispatchSelectTimeSlot(mainAppointmentTimeSlot);
    }

    if (isMultipleAppointment) {
      if (!relatedAppointmentTimeSlot) {
        dispatchSetRelatedAppointmentTimeSlot({ appointmentTypeId: relatedAppointmentTypeId });
      }

      if (relatedAppointmentTimeSlot) {
        dispatchSetRelatedAppointmentTimeSlot(relatedAppointmentTimeSlot);
      }
    }

    dispatchSaveAppointmentDraft();

    this.loadSlots();
  };

  handleAvailabilityError = ({ status }) => {
    const {
      mainAppointmentDraft: {
        shop: { name: selectedShopName },
      },
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;

    dispatchApiCallFinished(true);

    if (status === 404) {
      const message = errorMessages[NO_AVAILABLE_PARTNERS_ERROR];
      const actionMessage = {
        type: NO_AVAILABLE_PARTNERS_ERROR,
        title: message.createTitle(selectedShopName),
      };

      dispatchResetErrorMessagesWith([actionMessage]);
    }
  };

  saveSlot = () => {
    const {
      history,
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,

      dispatchSaveAppointmentDraft,
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
    } = this.props;

    if (!mainAppointmentDraft.dateSlot) {
      dispatchResetErrorMessagesWith([{ type: SLOT_DATE_NOT_SELECTED_ERROR }]);
    } else if (!mainAppointmentDraft.timeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else if (isMultipleAppointment && !relatedAppointmentTimeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else {
      dispatchApiCallFinished(false);
      dispatchSaveAppointmentDraft().then(() => {
        history.pushWithToken('/about-you');
      });
    }
  };

  loadSlotsSuccessHandler = (date) => {
    const {
      dispatchLoadPreviousSlots,
      dispatchLoadNextSlots,
      dispatchApiCallFinished,
      mainAppointmentDraft: {
        appointmentType: { maxAdvance },
      },
      config,
    } = this.props;

    const previousStartDate = dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(date, -1);
    const nextStartDate = dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(date, 1);

    if (dateUtils.isDateInCalendarRange(previousStartDate, maxAdvance)) {
      dispatchLoadPreviousSlots(previousStartDate, getEligibleSlotsFilter(config)).then(() =>
        dispatchApiCallFinished(true),
      );
    }
    if (dateUtils.isDateInCalendarRange(nextStartDate, maxAdvance)) {
      dispatchLoadNextSlots(nextStartDate, getEligibleSlotsFilter(config));
    }
  };

  loadSlots = () => {
    const {
      dispatchLoadInitialSlots,
      dispatchApiCallFinished,

      mainAppointmentDraft: { dateSlot: selectedDateSlot },
      config,
    } = this.props;

    dispatchApiCallFinished(false);

    const startDate = dateUtils.getSlotsStartDate(selectedDateSlot);
    const monthCountToDownload = selectedDateSlot ? 1 : 2;

    dispatchLoadInitialSlots(startDate, monthCountToDownload, getEligibleSlotsFilter(config)).then(
      () => {
        dispatchApiCallFinished(true);
        this.setState({
          isAjaxCompleted: true,
        });
        if (selectedDateSlot) {
          this.loadSlotsSuccessHandler(startDate);
        }
      },
    );
  };

  selectShop = (shop) => {
    const {
      dispatchSelectShop,
      dispatchSelectDateSlot,
      dispatchSelectPartner,
      dispatchClearErrorMessages,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadPartners,
      dispatchSetSlots,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    dispatchSelectPartner();
    dispatchSelectDateSlot();
    dispatchSetSlots([]);
    dispatchSelectShop(shop);
    triggerAvailabilityInteractionAnalytics('shopChange', { branchId: shop.id });
    dispatchUpdateAppointmentType()
      .then(() => dispatchLoadPartners())
      .then(this.loadSlots);
  };

  selectPartnersGroup = (group) => {
    const {
      partnersGroups,

      dispatchSelectPartnersGroup,
      dispatchSelectDateSlot,
      dispatchSelectPartner,
      dispatchClearErrorMessages,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadPartners,
      dispatchSetSlots,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    dispatchSelectPartner();
    dispatchSelectDateSlot();
    dispatchSetSlots([]);
    dispatchSelectPartnersGroup(group.id);
    triggerPartnerGroupChangeAnalytics(getPartnerGroupName(group.name, partnersGroups));
    dispatchUpdateAppointmentType()
      .then(() => dispatchLoadPartners())
      .then(this.loadSlots);
  };

  selectPartner = (partner) => {
    const { dispatchSelectPartner, dispatchSetSlots } = this.props;

    if (partner !== null) {
      dispatchSelectPartner(partner);

      // do not remove setTimeout it's one of the AC in BKM-227
      window.setTimeout(() => {
        dispatchSetSlots([]);
        this.checkSlotAvailability(partner.id).then(this.resolveSlots);
      }, 500);
      scrollToAnchor('chosen-partner');
    }
    triggerAvailabilityInteractionAnalytics('advisorChange');
  };

  render() {
    const {
      title,
      subtitle: { component: Subtitle },
      ShopsDropdown,
      mainAppointmentDraft,
      partners,
      filteredDateSlots,
      prevPath,
      externalPrevPath,
      ChosenPartner,
      GroupsDropdown,
      config: { Calendar = {}, remote, partnerTitle, ...restConfig },
      isRemoteConfigEnabled,
      shopList,
      partnersGroups,
      isApiCallFinished,
      isMultipleAppointment,
      dispatchLoadPreviousSlots,
      dispatchLoadNextSlots,
    } = this.props;

    const {
      appointmentType: { maxAdvance, duration, id: appointmentId },
      partner: selectedPartner,
      shop: { id: selectedShopId, name: selectedShopName },
      partnersGroupId: selectedPartnersGroupId,
    } = mainAppointmentDraft;

    const {
      calendarTimeSlotsIntro: CalendarTimeSlotsIntro,
      timeSlotsList,
      timeSlotSectionTitle,
      eligibleSlotsFilter = defaultEligibleSlotsFilter,
      formatTimeSlots,
    } = Calendar;

    const { isAjaxCompleted, subtitleLinkUrl } = this.state;
    const partnersGroupName = (
      partnersGroups.find((partnersGroup) => partnersGroup.id === selectedPartnersGroupId) || {}
    ).name;

    const { features = [] } = this.context || {};
    const isPhoneConsultationActive =
      isFeatureEnabled(features, 'phone_consultation') &&
      appointmentId === phoneConsultationAppointmentId;

    return (
      <section data-testid="SelectDate" data-test-ajax={isAjaxCompleted}>
        <div className={classnames({ [styles.Row]: isRemoteConfigEnabled || GroupsDropdown })}>
          {ShopsDropdown && !!shopList.length && (
            <ShopsDropdown
              selectedShopId={selectedShopId}
              shopList={shopList}
              onChange={this.selectShop}
              className={styles.Col}
            />
          )}
          {GroupsDropdown && (
            <GroupsDropdown
              partnersGroups={partnersGroups}
              selectedPartnersGroupId={selectedPartnersGroupId}
              onChange={this.selectPartnersGroup}
              className={styles.Col}
            />
          )}
          {!isMultipleAppointment && ChosenPartner && (
            <ChosenPartner
              partner={selectedPartner}
              partners={partners}
              selectPartner={this.selectPartner}
              selectedShopId={selectedShopId}
              isSeries={isRemoteConfigEnabled || !!GroupsDropdown}
              partnerTitle={partnersGroupName || partnerTitle}
              {...restConfig}
            />
          )}
        </div>
        <div className={styles.TitleAndCalendarWithFiltersContainer}>
          <ScrollToMessageTypeAnchor forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]}>
            <TitleWithSubtitle
              title={
                isPhoneConsultationActive ? `Select a date for your telephone appointment` : title
              }
              className={classnames(styles.Title, styles.TitleWithSubtitle, {
                [styles.TitleWithSubtitle]: isRemoteConfigEnabled,
              })}
            >
              {isPhoneConsultationActive
                ? null
                : Subtitle && (
                    <Subtitle
                      className={styles.Subtitle}
                      classNameForSubtitleWithLink={styles.SubtitleWithLink}
                      secondLineClassName={styles.Subtitle__second}
                      linkUrl={subtitleLinkUrl}
                    />
                  )}
            </TitleWithSubtitle>
          </ScrollToMessageTypeAnchor>
          <CalendarWithFilters
            selectedDateSlot={filteredDateSlots}
            timeSlotsList={timeSlotsList}
            timeSlotSectionTitle={timeSlotSectionTitle}
            isFiltersHidden={!!GroupsDropdown}
            loadNextSlots={dispatchLoadNextSlots}
            loadPreviousSlots={dispatchLoadPreviousSlots}
            maxAdvance={maxAdvance}
            eligibleSlotsFilter={eligibleSlotsFilter}
            selectedPartner={selectedPartner}
            formatTimeSlots={formatTimeSlots}
            isApiCallFinished={isApiCallFinished}
            extraClassName="SelectDateBordered"
          >
            <div className={styles.ErrorMessageContainer}>
              <Messages forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]} />
            </div>

            <Messages forTypes={[SLOT_TIME_NOT_SELECTED_ERROR]} />
            {CalendarTimeSlotsIntro && (
              <CalendarTimeSlotsIntro
                shopName={selectedShopName}
                duration={formatInHoursAndMinutes(msToMinutes(duration))}
              />
            )}
          </CalendarWithFilters>
        </div>
        <ScreenNav
          onNextClick={this.saveSlot}
          prevPath={prevPath}
          externalPrevPath={externalPrevPath}
        />
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    partners: { partners },
    loader: { isCallFinished },
    messages: {
      app: { errorMessages: slotErrorMessages },
    },
    envConfig,
  } = state;
  const mainAppointmentDraft = appointmentDraftSelectors.selectAppointmentDraft(state);

  const {
    appointmentType: { id: mainAppointmentTypeId },
  } = mainAppointmentDraft;

  const relatedAppointmentDraft = appointmentDraftSelectors.selectRelatedAppointmentDraftById()(
    state,
  ) || { appointmentType: {} };

  const selectedSlotErrorMessages = calendarSelectors.selectSlotMessages(slotErrorMessages);
  const remoteConfig = envConfigSelectors.remoteConfig(mainAppointmentTypeId)(state);
  const externalPrevPath =
    envConfigSelectors.selectExternalPrevPathForSelectDate(mainAppointmentTypeId)(state);
  const { inStoreAppointmentTypeId, virtualAppointmentTypeId } =
    envConfigSelectors.selectAppointmentTypeConfig(envConfig, mainAppointmentTypeId);
  const { virtualStoreId } = envConfigSelectors.selectAppointmentTypeConfig(
    envConfig,
    virtualAppointmentTypeId,
  );
  const chosenPartner = envConfigSelectors.selectChosenPartner(mainAppointmentTypeId)(state);
  const groupsDropdown = envConfigSelectors.selectGroupsDropdown(state);
  const prevPath = envConfigSelectors.selectDatePrevPath(mainAppointmentTypeId)(state);

  const subtitleComponent =
    envConfigSelectors.selectSubtitleComponent(mainAppointmentTypeId)(state);

  return {
    mainAppointmentDraft,
    relatedAppointmentTimeSlot: relatedAppointmentDraft.timeSlot,
    relatedAppointmentTypeId: relatedAppointmentDraft.appointmentType.id,
    partners,
    filteredDateSlots: selectDateSelectors.selectDateSlot(mainAppointmentTypeId)(state),
    config: envConfigSelectors.selectConfig(mainAppointmentTypeId)(state),
    ChosenPartner: chosenPartner,
    GroupsDropdown: groupsDropdown,
    externalPrevPath,
    prevPath: externalPrevPath ? null : prevPath,
    slotErrorMessages: selectedSlotErrorMessages,
    isRemoteConfigEnabled: isNotEmpty(remoteConfig),
    title:
      envConfigSelectors.selectTitleForSelectDate(mainAppointmentTypeId)(state) ||
      envConfigSelectors.selectTitleForSelectDate(mainAppointmentTypeId)(state) ||
      'Select a date',
    ShopsDropdown: remoteConfig.ShopsDropdown,
    shopList: shopsSelectors.selectShopList(state),
    searchQuery: journeyStateSelectors.selectSearchQuery(state),
    subtitle: subtitleComponent && {
      component: subtitleComponent,
      inStoreAppointmentTypeId,
      virtualAppointmentTypeId,
      virtualStoreId,
    },
    partnersGroups: appointmentDraftSelectors.selectPartnersGroups(state),
    isMultipleAppointment: appointmentDraftSelectors.isMultipleAppointment(state),
    isApiCallFinished: isCallFinished,
  };
};

const mapDispatchToProps = {
  dispatchLoadInitialSlots: loadInitialSlots,
  dispatchLoadPreviousSlots: loadPreviousSlots,
  dispatchLoadNextSlots: loadNextSlots,
  dispatchSetSlots: setSlots,

  dispatchSaveAppointmentDraft: appointmentDraftActions.saveAppointmentDraft,
  dispatchSelectDateSlot: appointmentDraftActions.setDateSlot,
  dispatchSelectTimeSlot: appointmentDraftActions.setTimeSlot,
  dispatchSelectPartner: appointmentDraftActions.setPartner,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchSelectPartnersGroup: appointmentDraftActions.setPartnersGroupId,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,
  dispatchSetRelatedAppointmentTimeSlot: appointmentDraftActions.setRelatedAppointmentTimeSlot,

  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchApiCallFinished: apiCallFinished,
  dispatchLoadPartners: loadPartners,
  dispatchLoadShops: shopsActions.loadShops,
};

export const ConnectedSelectDate = withRestorableDraft(
  withRequiredPrecondition(
    connect(mapStateToProps, mapDispatchToProps)(withProgressBar(SelectDate)),
  ),
);

const groupsModel = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
};

const appointmentTypeModel = {
  id: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  minAdvance: PropTypes.number.isRequired,
  maxAdvance: PropTypes.number.isRequired,
  localId: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape(groupsModel)),
  hasGroups: PropTypes.bool.isRequired,
};

const shopModel = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

const partnerModel = {};

const timeSlotModel = {
  eventId: PropTypes.string.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
};

const dateSlotModel = {
  date: PropTypes.string.isRequired,
  times: PropTypes.arrayOf(PropTypes.shape(timeSlotModel)),
};

SelectDate.propTypes = {
  history: PropTypes.shape({}).isRequired,
  isApiCallFinished: PropTypes.bool.isRequired,
  isMultipleAppointment: PropTypes.bool.isRequired,

  relatedAppointmentTimeSlot: PropTypes.shape(timeSlotModel),
  relatedAppointmentTypeId: PropTypes.string,

  mainAppointmentDraft: PropTypes.shape({
    appointmentType: PropTypes.shape(appointmentTypeModel).isRequired,
    shop: PropTypes.shape(shopModel).isRequired,
    partner: PropTypes.shape(partnerModel).isRequired,
    dateSlot: PropTypes.string.isRequired,
    timeSlot: PropTypes.shape(timeSlotModel).isRequired,
  }).isRequired,
  partners: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  filteredDateSlots: PropTypes.shape(dateSlotModel),
  config: PropTypes.shape({
    partnerTitle: PropTypes.string,
    Calendar: PropTypes.shape({
      calendarTimeSlotsIntro: PropTypes.func,
      timeSlotsList: PropTypes.func,
      timeSlotSectionTitle: PropTypes.string,
      formatTimeSlots: PropTypes.func,
    }),
    remote: PropTypes.shape({
      appointment: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
  ChosenPartner: PropTypes.func,
  GroupsDropdown: PropTypes.func,
  prevPath: PropTypes.string,
  externalPrevPath: PropTypes.string,
  slotErrorMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  isRemoteConfigEnabled: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.shape({
    component: PropTypes.func,
    inStoreAppointmentTypeId: PropTypes.string,
    virtualAppointmentTypeId: PropTypes.string,
    virtualStoreId: PropTypes.string,
  }),
  ShopsDropdown: PropTypes.func,
  shopList: PropTypes.arrayOf(PropTypes.shape(shopModel)),
  searchQuery: PropTypes.string.isRequired,
  partnersGroups: PropTypes.arrayOf(PropTypes.shape(groupsModel)),

  dispatchSelectDateSlot: PropTypes.func.isRequired,
  dispatchSelectTimeSlot: PropTypes.func.isRequired,
  dispatchSelectPartner: PropTypes.func.isRequired,
  dispatchSelectPartnersGroup: PropTypes.func.isRequired,
  dispatchSetSlots: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchSaveAppointmentDraft: PropTypes.func.isRequired,
  dispatchLoadInitialSlots: PropTypes.func.isRequired,
  dispatchLoadPreviousSlots: PropTypes.func.isRequired,
  dispatchLoadNextSlots: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchLoadPartners: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
  dispatchLoadShops: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchSetRelatedAppointmentTimeSlot: PropTypes.func.isRequired,
};

SelectDate.defaultProps = {
  filteredDateSlots: {
    date: '',
    times: [],
  },
  isRemoteConfigEnabled: false,
  subtitle: {},
  ShopsDropdown: null,
  shopList: [],
  prevPath: null,
  externalPrevPath: null,
  ChosenPartner: undefined,
  GroupsDropdown: undefined,
  partnersGroups: [],
  relatedAppointmentTimeSlot: null,
  relatedAppointmentTypeId: null,
};
